import { useState } from "react";
import { api, ApiError, type User } from "../lib/api";

export function Auth({ onAuthed }: { onAuthed: (user: User) => void }) {
  const [mode, setMode] = useState<"in" | "up" | "guest">("in");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const signUp = mode === "up";
  const asGuest = mode === "guest";

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setFieldErrors({});

    try {
      const { user } = asGuest
        ? await api.guest(displayName)
        : signUp
          ? await api.register(email, password, displayName)
          : await api.login(email, password);
      onAuthed(user);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setFieldErrors(err.fieldErrors ?? {});
      } else {
        setError("Couldn't reach the server.");
      }
      setBusy(false);
    }
  }

  const fieldError = (name: string) => fieldErrors[name]?.[0];

  return (
    <div className="auth-wrap">
      <div className="card auth-card">
        <div className="brand" style={{ marginBottom: 20 }}>
          <span className="dot" />
          rhythm
        </div>

        <h1>{asGuest ? "Just visiting" : signUp ? "Make a room" : "Welcome back"}</h1>
        <p className="sub">
          {asGuest
            ? "Pick a name and go. Nothing to sign up for."
            : signUp
              ? "Rooms you can decorate, with everyone on screen at once."
              : "Sign in to get back to your rooms."}
        </p>

        <form onSubmit={submit}>
          {(signUp || asGuest) && (
            <div className="field">
              <label htmlFor="display_name">What should people call you?</label>
              <input
                id="display_name"
                className="input"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Rhythm"
                maxLength={40}
                required
                autoComplete="nickname"
              />
              {fieldError("display_name") && (
                <p className="error">{fieldError("display_name")}</p>
              )}
              <p className="hint">
                {asGuest
                  ? "This is what everyone in a room sees. You can change it later."
                  : "This is what everyone in a room sees. Your email stays private."}
              </p>
            </div>
          )}

          {asGuest && (
            <p className="hint" style={{ marginTop: -4, marginBottom: 14 }}>
              Staying signed in relies on this browser alone — clear its data or
              switch device and the account is gone, along with any rooms you
              made. Create a real account if you want them back later.
            </p>
          )}

          {!asGuest && (
          <div className="field">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              className="input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
            {fieldError("email") && <p className="error">{fieldError("email")}</p>}
          </div>
          )}

          {!asGuest && (
          <div className="field">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              className="input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete={signUp ? "new-password" : "current-password"}
            />
            {fieldError("password") && <p className="error">{fieldError("password")}</p>}
            {signUp && <p className="hint">At least 12 characters.</p>}
          </div>
          )}

          {error && <p className="error">{error}</p>}

          <button className="btn primary" type="submit" disabled={busy} style={{ width: "100%", marginTop: 6 }}>
            {busy ? (
              <span className="spinner" />
            ) : asGuest ? (
              "Continue"
            ) : signUp ? (
              "Create account"
            ) : (
              "Sign in"
            )}
          </button>
        </form>

        {!asGuest && (
          <>
            <div className="or-rule">
              <span>or</span>
            </div>
            <button
              type="button"
              className="btn"
              style={{ width: "100%" }}
              onClick={() => {
                setMode("guest");
                setError(null);
                setFieldErrors({});
              }}
            >
              Continue as guest
            </button>
          </>
        )}

        <div className="switch">
          {asGuest ? (
            <>
              Want to keep your rooms?{" "}
              <button
                type="button"
                onClick={() => {
                  setMode("up");
                  setError(null);
                  setFieldErrors({});
                }}
              >
                Create an account
              </button>
            </>
          ) : (
            <>
              {signUp ? "Already have an account? " : "New here? "}
              <button
                type="button"
                onClick={() => {
                  setMode(signUp ? "in" : "up");
                  setError(null);
                  setFieldErrors({});
                }}
              >
                {signUp ? "Sign in" : "Create one"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
